/**
 * Per-line conflict resolver for CSV import (Phase 5.4a-csv-conflicts).
 *
 * Renders an expandable list of conflicting rows. Each row shows:
 *   - meta (reference, designation, match type)
 *   - per-field choice : Garder existant / Prendre CSV / Corriger (input)
 *   - per-axis quantity choice : Additionner / Remplacer / Garder / Corriger
 *   - bulk shortcuts ("appliquer à toutes les lignes avec même conflit")
 *
 * Props:
 *   conflicts        – array (from /import-preview response.conflicts)
 *   decisions        – controlled state {row_index → decision}
 *   onChange         – (decisions) => void
 *   bulkSimilarApply – (conflict_type, partial_decision) => void
 *   bulkAllApply     – (partial_decision) => void
 *   maxRows          – soft cap, hides per-line beyond this (default 1000)
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Edit3, Check, Boxes, Plus, ArrowLeftRight, Minus, RotateCcw } from 'lucide-react';

const QTY_ACTION_LABELS = {
  add: { label: 'Additionner', hint: 'existant + CSV', icon: Plus },
  replace: { label: 'Remplacer', hint: 'CSV écrase', icon: ArrowLeftRight },
  keep: { label: 'Garder', hint: 'ignore CSV', icon: Minus },
};

const FIELD_LABELS = {
  designation: 'Désignation',
  categorie: 'Catégorie',
  fournisseur: 'Fournisseur',
  prix_achat: 'Prix achat',
  prix_vente: 'Prix vente',
  ean: 'EAN13',
  emplacement: 'Emplacement',
  seuil_alerte: 'Seuil alerte',
};

const CONFLICT_TYPE_BADGE = {
  ean_match: { label: 'EAN', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  ref_match: { label: 'Réf.', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  designation_match: { label: 'Désignation', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
};

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return <span className="text-gray-600">∅</span>;
  if (typeof v === 'number') return <span className="font-mono">{Number.isInteger(v) ? v : v.toFixed(2)}</span>;
  return String(v);
};

const ConflictRow = ({ c, decision, setDecision, onApplyToSimilar }) => {
  const [open, setOpen] = useState(false);
  const fields = c.conflict_fields || [];
  const badge = CONFLICT_TYPE_BADGE[c.conflict_type] || CONFLICT_TYPE_BADGE.ref_match;
  const fieldChoices = decision.field_choices || {};
  const qbAction = decision.qty_boutique_action ?? 'add';
  const qdAction = decision.qty_depot_action ?? 'add';

  const update = (patch) => setDecision({ ...decision, ...patch });
  const updateFC = (field, value) => update({ field_choices: { ...fieldChoices, [field]: value } });
  const qbResolved = useMemo(() => {
    if (typeof qbAction === 'number') return qbAction;
    if (qbAction === 'replace') return c.qty_imported_boutique;
    if (qbAction === 'keep') return c.qty_existing_boutique;
    return c.qty_existing_boutique + c.qty_imported_boutique;
  }, [qbAction, c.qty_existing_boutique, c.qty_imported_boutique]);
  const qdResolved = useMemo(() => {
    if (typeof qdAction === 'number') return qdAction;
    if (qdAction === 'replace') return c.qty_imported_depot;
    if (qdAction === 'keep') return c.qty_existing_depot;
    return c.qty_existing_depot + c.qty_imported_depot;
  }, [qdAction, c.qty_existing_depot, c.qty_imported_depot]);

  const isAction = (a) => decision.action === a;

  return (
    <div className="border border-white/10 rounded-xl bg-white/[0.02] overflow-hidden" data-testid={`conflict-row-${c.row_index}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        <span className="text-[11px] text-gray-500 font-mono">L{c.row_index}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
        <span className="text-sm text-white truncate flex-1">{c.row.designation || c.existing.designation}</span>
        <span className="text-[11px] text-gray-500 hidden sm:block">{c.row.reference || c.existing.reference}</span>
        <span className="text-[11px] text-gray-500">{fields.length} champ(s) différent(s)</span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3">
          {/* Action selector */}
          <div className="flex flex-wrap gap-1.5 text-xs">
            {[
              { id: 'update', label: '🔄 Mettre à jour' },
              { id: 'skip', label: '⏭️ Ignorer' },
              { id: 'duplicate', label: '➕ Dupliquer (EAN auto)' },
              { id: 'merge', label: '🔗 Fusionner (somme stocks)' },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => update({ action: opt.id })}
                className={`px-2.5 py-1 rounded-lg border transition-colors ${
                  isAction(opt.id)
                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-100'
                    : 'bg-white/[0.04] border-white/10 text-gray-400 hover:bg-white/[0.08]'
                }`}
                data-testid={`action-${opt.id}-${c.row_index}`}
              >
                {opt.label}
              </button>
            ))}
            {/* Apply to all similar */}
            {onApplyToSimilar && (
              <button
                onClick={() => onApplyToSimilar(c.conflict_type, { action: decision.action || 'update' })}
                className="ml-auto px-2.5 py-1 rounded-lg border border-white/10 bg-white/5 text-gray-400 hover:text-white text-[11px]"
                title={`Appliquer à toutes les lignes avec le même conflit (${badge.label})`}
              >
                Appliquer à tous les conflits {badge.label}
              </button>
            )}
          </div>

          {/* Field-by-field diff (only when action=update) */}
          {(decision.action === 'update' || !decision.action) && fields.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left py-1.5 px-2 font-medium w-1/5">Champ</th>
                    <th className="text-left py-1.5 px-2 font-medium w-1/4">Existant</th>
                    <th className="text-left py-1.5 px-2 font-medium w-1/4">CSV</th>
                    <th className="text-left py-1.5 px-2 font-medium">Choix</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => {
                    const choice = fieldChoices[f.field] ?? 'csv';
                    const isCustom = typeof choice === 'string' && choice !== 'csv' && choice !== 'existing';
                    return (
                      <tr key={f.field} className="border-t border-white/5">
                        <td className="py-1.5 px-2 text-gray-300">{FIELD_LABELS[f.field] || f.field}</td>
                        <td className="py-1.5 px-2 text-gray-400">{fmt(f.existing)}</td>
                        <td className="py-1.5 px-2 text-emerald-300">{fmt(f.imported)}</td>
                        <td className="py-1.5 px-2">
                          <div className="flex gap-1 items-center flex-wrap">
                            <button
                              onClick={() => updateFC(f.field, 'existing')}
                              className={`px-1.5 py-0.5 rounded text-[10px] ${choice === 'existing' ? 'bg-blue-500/25 text-blue-200 ring-1 ring-blue-500/40' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                              data-testid={`fc-existing-${c.row_index}-${f.field}`}
                            >
                              Garder
                            </button>
                            <button
                              onClick={() => updateFC(f.field, 'csv')}
                              className={`px-1.5 py-0.5 rounded text-[10px] ${choice === 'csv' ? 'bg-emerald-500/25 text-emerald-200 ring-1 ring-emerald-500/40' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                              data-testid={`fc-csv-${c.row_index}-${f.field}`}
                            >
                              CSV
                            </button>
                            <input
                              type="text"
                              defaultValue={isCustom ? choice : ''}
                              placeholder="Corriger…"
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v) updateFC(f.field, v);
                                else if (isCustom) updateFC(f.field, 'csv');
                              }}
                              className={`px-1.5 py-0.5 rounded text-[10px] w-24 bg-black/30 border ${isCustom ? 'border-amber-400/50 text-amber-100' : 'border-white/10 text-gray-300'}`}
                              data-testid={`fc-custom-${c.row_index}-${f.field}`}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Quantities — always shown (key resolution) */}
          {(decision.action === 'update' || !decision.action) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { axis: 'boutique', label: 'Qté boutique', existing: c.qty_existing_boutique, imported: c.qty_imported_boutique, action: qbAction, resolved: qbResolved, setter: (v) => update({ qty_boutique_action: v }) },
                { axis: 'depot', label: 'Qté dépôt', existing: c.qty_existing_depot, imported: c.qty_imported_depot, action: qdAction, resolved: qdResolved, setter: (v) => update({ qty_depot_action: v }) },
              ].map(({ axis, label, existing, imported, action, resolved, setter }) => (
                <div key={axis} className="bg-white/[0.04] border border-white/10 rounded-lg p-2">
                  <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
                    <span className="font-semibold">{label}</span>
                    <span className="text-gray-500">existant <strong className="text-white">{existing}</strong> • CSV <strong className="text-emerald-300">{imported}</strong></span>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {Object.entries(QTY_ACTION_LABELS).map(([k, v]) => {
                      const Icon = v.icon;
                      return (
                        <button
                          key={k}
                          onClick={() => setter(k)}
                          className={`px-2 py-1 rounded text-[10px] flex items-center gap-1 ${
                            action === k
                              ? 'bg-cyan-500/20 text-cyan-200 ring-1 ring-cyan-500/50'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                          data-testid={`qty-${axis}-${k}-${c.row_index}`}
                        >
                          <Icon className="w-3 h-3" /> {v.label}
                        </button>
                      );
                    })}
                    <input
                      type="number"
                      placeholder="Corriger"
                      defaultValue={typeof action === 'number' ? action : ''}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isNaN(n) && n >= 0) setter(n);
                        else if (typeof action === 'number') setter('add');
                      }}
                      className={`px-1.5 py-0.5 rounded text-[10px] w-16 bg-black/30 border ${typeof action === 'number' ? 'border-amber-400/50 text-amber-100' : 'border-white/10 text-gray-300'}`}
                      data-testid={`qty-${axis}-custom-${c.row_index}`}
                    />
                  </div>
                  <div className="mt-1 text-[10px] text-gray-500">
                    Résultat final : <strong className="text-white">{resolved}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CSVConflictResolver = ({
  conflicts = [],
  decisions = {},
  onChange,
  onBulkSimilar,
  onBulkAll,
  maxRows = 1000,
}) => {
  const tooMany = conflicts.length > maxRows;

  const setRowDecision = (rowIndex, decision) => {
    onChange({ ...decisions, [rowIndex]: decision });
  };

  return (
    <div className="space-y-2" data-testid="conflict-resolver">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-400">
          {conflicts.length} conflit(s) — chaque ligne peut être traitée individuellement.
        </span>
        {!tooMany && (
          <div className="flex gap-1 text-[11px]">
            <button
              onClick={() => onBulkAll?.({ action: 'update' })}
              className="px-2 py-1 rounded bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
              data-testid="bulk-all-update"
            >
              <RotateCcw className="w-3 h-3 inline mr-1" />Tout en MAJ
            </button>
            <button
              onClick={() => onBulkAll?.({ action: 'skip' })}
              className="px-2 py-1 rounded bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
              data-testid="bulk-all-skip"
            >
              <Minus className="w-3 h-3 inline mr-1" />Tout ignorer
            </button>
          </div>
        )}
      </div>

      {tooMany ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-amber-200 text-xs">
          Plus de {maxRows} conflits — mode ligne par ligne désactivé. Revenez à la stratégie globale.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
          {conflicts.map((c) => (
            <ConflictRow
              key={c.row_index}
              c={c}
              decision={decisions[c.row_index] || { action: 'update' }}
              setDecision={(d) => setRowDecision(c.row_index, d)}
              onApplyToSimilar={(type, partial) => onBulkSimilar?.(type, partial)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CSVConflictResolver;
